import React, { useEffect, useState, useRef } from "react";
import {
  MapContainer,
  TileLayer,
  GeoJSON,
  CircleMarker,
  Tooltip,
} from "react-leaflet";
import "leaflet/dist/leaflet.css";
import { fetchMapData } from "../../Utils/cityProfileUtils";

import FitBoundsToData from "./utils/leaflet/FitBoundsToData";
import ZoomToFeature from "./utils/leaflet/ZoomToFeature";
import Legend from "./utils/leaflet/Legend";
import InfoTooltip from "./utils/leaflet/InfoTooltip";
import useApi from "../../Hooks/useApi";
import { isAuthenticated } from "../../Helpers/authHelper";

const CityMap = ({ city }) => {
  
  const [wardData, setWardData] = useState(null);
  const [pointsData, setPointsData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeWard, setActiveWard] = useState(null);
  const [activePointType, setActivePointType] = useState(null);
  const [selectedWardFeature, setSelectedWardFeature] = useState(null);
  const [zoomLevel, setZoomLevel] = useState(0);
  const mapRef = useRef(null);
  const { request } = useApi({ isPublic: !isAuthenticated() });

  const pointColors = {
    "Public Toilet": "#FCBD49",
    "Dustbin-Informal": "#C8656C",
    "Dustbin-Formal": "#2C9595",
    "Informal Dumping Site": "#CEDC47",
    Slum: "#FF0000",
    "Dumping Site": "#595EA9",
    "Secondary Transfer Station": "#F0D994",
    "Treatment Plant": "#4A5544",
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const data = await fetchMapData(
          request,
          city?.city?.id,
          city?.dataSource[0]?.surveyId
        );

        if (data) {
          setWardData(data?.wards || []);
          setPointsData(data?.city || []);
        }

        setLoading(false);
      } catch (error) {
        console.error("Error fetching data", error);
        setLoading(false);
      }
    };
    if (city?.city && city?.year && city?.dataSource) {
      fetchData();
    } else {
      setWardData(null);
      setPointsData([]);
    }
  }, [city, request]);

  const wardStyle = () => ({
    fillColor: "#2F4B7C",
    weight: 2,
    opacity: 1,
    color: "#2F4B7C",
    fillOpacity: 0.7,
  });

  const activeWardStyle = () => ({
    fillColor: "#2F4B7C",
    weight: 3,
    opacity: 1,
    color: "#2F4B7C",
    fillOpacity: 1,
    "z-index": 0,
  });

  const onWardMouseOver = (e) => {
    const layer = e.target;
    const { ward, population, area_sqkm } = e.target.feature.properties;
    layer.setStyle(activeWardStyle());
    setActiveWard(`<strong>Ward:</strong> ${ward}<br/>
                        <strong>Population:</strong> ${Number(population).toLocaleString()}<br/>
                        <strong>Area:</strong> ${area_sqkm?.toFixed(2)} Sq Km`);
  };

  const onWardMouseOut = (e) => {
    const layer = e.target;
    layer.setStyle(wardStyle());
    setActiveWard(null);
  };

  const onWardClick = (e) => {
    const feature = e.target.feature;
    setSelectedWardFeature(feature);
    setZoomLevel((prev) => prev + 1);
    const { ward, population, area_sqkm } = feature.properties;
    setActiveWard(`<strong>Ward:</strong> ${ward}<br/>
                        <strong>Population:</strong> ${Number(
      population
    ).toLocaleString()}<br/>
                        <strong>Area:</strong> ${area_sqkm?.toFixed(2)} Sq Km`);
  };


  const filteredPoints = activePointType
    ? pointsData.filter((point) => point.pointType === activePointType)
    : pointsData;

  const pointTypeOrder = [
    "Public Toilet",
    "Dustbin-Informal",
    "Dustbin-Formal",
    "Informal Dumping Site",
    "Slum",
    "Dumping Site",
    "Secondary Transfer Station",
    "Treatment Plant",
  ];
  const availableTypes = new Set(pointsData.map((point) => point.pointType));

  const legendItems = pointTypeOrder
    .filter((type) => availableTypes.has(type)) // only show types that exist
    .map((type) => ({
      type,
      color: pointColors[type] || "#999",
    }));
  /*   if (loading) {
    return (
      <div style={{ textAlign: "center", marginTop: "2rem" }}>
        Loading map data...
      </div>
    );
  }
 */

  useEffect(() => {
    if (mapRef.current) {
      setTimeout(() => {
        mapRef.current?.invalidateSize();
      }, 200);
    }
  }, [wardData, pointsData]);

  return (
    <div style={{ width: "100%", height: "50vh", position: "relative" }}>
      <MapContainer
        center={[23.4607, 91.1809]}
        zoom={12}
        style={{ height: "100%", width: "100%", zIndex: 0 }}
        ref={mapRef}
        zoomControl={false}
      >
        <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
        {wardData && wardData.features && wardData.features?.length > 0 && (
          <>
            <GeoJSON
              data={wardData}
              style={wardStyle}
              onEachFeature={(feature, layer) => {
                layer.on({
                  mouseover: onWardMouseOver,
                  mouseout: onWardMouseOut,
                  click: onWardClick,
                });
              }}
            />
            <FitBoundsToData data={wardData} />
          </>
        )}

        {filteredPoints &&
          filteredPoints.length > 0 &&
          filteredPoints.map((point) => (
            <CircleMarker
              key={point.id}
              center={[point.lat, point.lng]}
              radius={6}
              pathOptions={{
                fillColor: pointColors[point.pointType] || "#999",
                color: "#fff",
                weight: 1,
                opacity: 1,
                fillOpacity:
                  activePointType === point.pointType || !activePointType
                    ? 1
                    : 0.4,
              }}
            >
              <Tooltip>
                {point.feature} {point.type ? `(${point.type})` : ""}
              </Tooltip>
            </CircleMarker>
          ))}

        {selectedWardFeature && (
          <ZoomToFeature feature={selectedWardFeature} zoomLevel={zoomLevel} />
        )}
      </MapContainer>

      {/* Fallback label if no data */}
      {wardData?.features?.length === 0 &&
        pointsData?.length === 0 &&
        !loading && (
          <div
            style={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              background: "rgba(255,255,255,0.9)",
              padding: "1rem 2rem",
              borderRadius: "8px",
              fontWeight: "bold",
              zIndex: 0,
            }}
          >
            No data found for this city
          </div>
        )}
      {/* Insufficient selection */}
      {!loading && (!city?.city || !city?.year || !city?.dataSource) ? (
        <div
          style={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            background: "rgba(255,255,255,0.9)",
            padding: "1rem 2rem",
            borderRadius: "8px",
            fontWeight: "bold",
            zIndex: 0,
          }}
        >
          {!city?.city
            ? "Select City, Year & Datasource to view map"
            : !city?.year
              ? "Select Year & Datasource to view map"
              : "Select Datasource to view map"}
        </div>
      ) : (
        <></>
      )}

      {activeWard && <InfoTooltip text={activeWard} />}

      {legendItems && legendItems.length > 0 && (
        <Legend
          items={legendItems}
          onHoverStart={setActivePointType}
          onHoverEnd={() => setActivePointType(null)}
        />
      )}
    </div>
  );
};

export default CityMap;
